import React, { useState, useEffect } from 'react';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';
import { useSoftKeyboard } from '../../contexts/SoftKeyboardContext';
import type { Customer } from '../../types';
import PlusIcon from '../icons/PlusIcon';
import XCircleIcon from '../icons/XCircleIcon';

interface CustomerSelectModalProps {
  customers: Customer[];
  onSelect: (customer: Customer | null) => void;
  onClose: () => void;
  onCreateCustomer?: (customer: Omit<Customer, 'id'>) => Promise<Customer>;
}

const CustomerSelectModal: React.FC<CustomerSelectModalProps> = ({ customers, onSelect, onClose, onCreateCustomer }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    phone: '',
    email: '',
    notes: '',
  });
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const { closeKeyboard } = useSoftKeyboard();

  // Don't auto-open soft keyboard when modal opens; only on field focus
  useEffect(() => {
    closeKeyboard();
  }, [closeKeyboard]);

  const searchField = useSoftKeyboardField({
    id: 'customer-search',
    type: 'alphanumeric',
    label: 'Search customer',
    value: searchTerm,
    onChange: setSearchTerm,
  });

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.phone.includes(searchTerm)
  );

  const handleNewCustomerChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewCustomerData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newCustomerData.name.trim() || !newCustomerData.phone.trim()) {
      setError('Name and phone are required');
      return;
    }

    if (!onCreateCustomer) {
      setError('Customer creation is not available');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      const newCustomer = await onCreateCustomer({
        ...newCustomerData,
        loyaltyPoints: 0,
      });
      
      // Select the newly created customer
      onSelect(newCustomer);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create customer');
    } finally {
      setIsCreating(false);
    }
  };

  if (showNewCustomerForm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">Add New Customer</h2>
            <button
              type="button"
              onClick={() => {
                closeKeyboard();
                setShowNewCustomerForm(false);
                setNewCustomerData({ name: '', phone: '', email: '', notes: '' });
                setError('');
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>
          <form onSubmit={handleCreateCustomer} className="p-4">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            <div className="mb-4">
              <label htmlFor="new-customer-name" className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="new-customer-name"
                name="name"
                value={newCustomerData.name}
                onChange={handleNewCustomerChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                placeholder="Customer name"
                required
              />
            </div>
            <div className="mb-4">
              <label htmlFor="new-customer-phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="new-customer-phone"
                name="phone"
                value={newCustomerData.phone}
                onChange={handleNewCustomerChange}
                inputMode="numeric"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                placeholder="Phone number"
                required
              />
            </div>
            <div className="mb-6">
              <label htmlFor="new-customer-email" className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-gray-400 text-xs">(optional)</span>
              </label>
              <input
                type="email"
                id="new-customer-email"
                name="email"
                value={newCustomerData.email}
                onChange={handleNewCustomerChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                placeholder="Email address"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  closeKeyboard();
                  setShowNewCustomerForm(false);
                  setNewCustomerData({ name: '', phone: '', email: '', notes: '' });
                  setError('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={isCreating}
              >
                {isCreating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden my-auto">
        <header className="p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800">Select Customer</h2>
          <input
            type="text"
            placeholder="Search by name or phone..."
            value={searchTerm}
            {...searchField}
            className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
          />
        </header>
        <main className="flex-1 overflow-y-auto p-4">
          {filteredCustomers.length === 0 && searchTerm ? (
            <div className="text-center py-8 text-gray-500">
              <p>No customers found matching "{searchTerm}"</p>
              {onCreateCustomer && (
                <button
                  type="button"
                  onClick={() => {
                    closeKeyboard();
                    setNewCustomerData(prev => ({ ...prev, name: searchTerm }));
                    setShowNewCustomerForm(true);
                  }}
                  className="mt-4 px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600 flex items-center gap-2 mx-auto"
                >
                  <PlusIcon className="w-5 h-5" />
                  Create "{searchTerm}"
                </button>
              )}
            </div>
          ) : (
            <ul>
              {filteredCustomers.map(customer => (
                <li key={customer.id}>
                  <button
                    onClick={() => onSelect(customer)}
                    className="w-full text-left p-3 mb-2 rounded-md hover:bg-gray-100"
                  >
                    <p className="font-semibold text-gray-800">{customer.name}</p>
                    <p className="text-sm text-gray-600">{customer.phone}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </main>
        <footer className="p-4 border-t flex justify-between gap-2">
          {onCreateCustomer && (
            <button
              onClick={() => setShowNewCustomerForm(true)}
              className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600 flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              Add New Customer
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button onClick={() => onSelect(null)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
              Continue as Guest
            </button>
            <button onClick={onClose} className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">
              Cancel
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default CustomerSelectModal;
